import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { Auth, User } from '@angular/fire/auth';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { filter, from, map, Observable, switchMap, tap } from 'rxjs';

import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSelectModule } from '@angular/material/select';
import { Timestamp } from '@angular/fire/firestore';
import { FAQS } from '@app/core/models/data';
import { ApiService } from '@app/core/services/api.service';
import { AuthService } from '@app/core/services/auth.service';
import { PaymentService } from '@app/core/services/payment.service';
import { IUser } from '@app/core/models/interfaces';

export interface Transaction {
  date: string;
  orderId: string;
  item: string;
  qty: number;
  amount: string;
  status: string;
}

@Component({
    selector: 'app-settings-dialog',
    imports: [
        CommonModule,
        MatDividerModule,
        MatButtonModule,
        MatIconModule,
        MatDialogModule,
        FormsModule,
        MatInputModule,
        MatFormFieldModule,
        MatProgressSpinnerModule,
        MatTooltipModule,
        MatCardModule,
        MatTableModule,
        MatExpansionModule,
        MatSelectModule,
        RouterModule,
    ],
    templateUrl: './settings-dialog.component.html',
    styleUrl: './settings-dialog.component.css'
})
export class SettingsDialogComponent implements OnInit {
  menus = [
    { key: 'profile', label: 'Profile', icon: 'person' },
    { key: 'billing', label: 'Credits & Billing', icon: 'credit_card' },
    { key: 'orders', label: 'Orders', icon: 'shopping_cart' },
    { key: 'support', label: 'Support', icon: 'support_agent' },
    { key: 'faqs', label: 'FAQs', icon: 'forum' },
    { key: 'feedback', label: 'Submit Feedback', icon: 'feedback' },
  ];

  displayedColumns: string[] = [
    'date',
    'orderId',
    'item',
    'qty',
    'amount',
    'status',
  ];

  dataSource: Transaction[] = [];

  tickets$: Observable<any[]> | null = null;
  faqs = FAQS;

  feedback = {
    projectId: null,
    rating: 0,
    message: '',
  };

  projects$: Observable<any[]> | null = null;
  deletedProjects$: Observable<any[]> | null = null;
  feedback$: Observable<any[]> | null = null;

  selectedMenu: any = null;
  selectedMenuIndex: number | null = null;

  savingUsername = false;
  usernameChanged = false;

  username: any;
  userEmail: any;
  user$!: Observable<any>;
  userCredits!: number;
  userId!: string;

  paymentStatusMessage: string | null = null;
  isProcessingCredits = false;
  userImage: string | null | undefined;

  constructor(
    public dialogRef: MatDialogRef<SettingsDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: { selectedItem: string },
    private api: ApiService,
    private auth: AuthService,
    private dialog: MatDialog,
    private paymentService: PaymentService,
    private router: Router,
  ) {}

  // Add this helper method to your class
  isMenuDisabled(menuKey: string, isLoggedIn: boolean): boolean {
    const restrictedMenus = ['profile', 'orders', 'support', 'feedback'];
    return !isLoggedIn && restrictedMenus.includes(menuKey);
  }

  navigateToLogin() {
    // 1. Close the dialog immediately
    this.dialogRef.close();

    // 2. Wait 1 second (1000ms) before navigating
    setTimeout(() => {
      this.router.navigate(['/login']);
    }, 500);
  }

  ngOnInit(): void {
    this.user$ = this.auth.userDoc$(); // directly get Firestore user doc (if your service is set up this way)

    this.user$.subscribe(async (userDoc: IUser) => {
      if (userDoc) {
        this.userId = userDoc.uid || '';
        this.username = userDoc.displayName || 'No User';
        this.userEmail = userDoc.email;
        this.userImage = userDoc.photoURL;
        console.log(this.userImage);
        this.userCredits = userDoc.credits || 0;
        this.menus[0].label = this.username;
        this.tickets$ = this.api.getMyTickets();
        this.projects$ = this.api.getMyProjects();
        this.feedback$ = this.api.getMyFeedback();

        // Fetch payments dynamically
        const payments = await this.paymentService.getUserPayments(this.userId);
        this.dataSource = payments.map((p: any) => {
          const createdOn = p.createdOn as Timestamp;
          return {
            date: createdOn.toDate().toDateString(), // "Mon Sep 23 2025"
            orderId: p.orderId,
            item: '100 Credits Bundle',
            qty: 1,
            amount: `Rs ${p.amount / 100}`, // Razorpay sends paise
            status:
              p.status === 'success' ? 'Payment Successful' : 'Payment Failed',
          } as Transaction;
        });
      }
    });

    // this.data.user$.subscribe((user: User | null) => {
    //   console.log(user);
    //   this.username = user?.displayName || 'No User';
    //   this.menus[0].label = this.username;
    // });

    // Determine initial tab based on passed selectedItem
    const keyToFind = (this.data?.selectedItem || '').toLowerCase();
    let initialIndex = this.menus.findIndex(
      (m) => m.key.toLowerCase() === keyToFind,
    );

    // Fallback to first tab if no match found
    if (initialIndex === -1) {
      initialIndex = 0;
    }

    this.selectMenu(this.menus[initialIndex], initialIndex);

    this.deletedProjects$ = this.auth.user.pipe(
      filter((user): user is User => !!user),
      switchMap((user) => from(this.api.getMyProjects())),
      map((projects) => {
        return projects
          .filter(
            (p: any) => p.isDeleted === true || p.permanently_removed === true,
          )
          .sort((a: any, b: any) => {
            const aVal = a.permanently_removed ? 1 : 0;
            const bVal = b.permanently_removed ? 1 : 0;
            return aVal - bVal;
          });
      }),
      tap((projects) => console.log('Sorted Deleted Projects:', projects)),
    );
  }

  onUsernameChange() {
    this.usernameChanged = true;
  }

  async saveUsername() {
    if (!this.usernameChanged || this.savingUsername) return;

    this.savingUsername = true;
    try {
      await this.api.updateUser({ displayName: this.username });

      this.usernameChanged = false; // reset state
    } catch (err) {
      console.error('Failed to update username:', err);
    } finally {
      this.savingUsername = false;
    }
  }

  async submitFeedback(form: any) {
    if (!this.feedback.message || this.feedback.rating === 0) {
      this.api['snack'].showError(
        'Please provide a rating and feedback message.',
      );
      return;
    }

    try {
      await this.api.createFeedback(
        this.feedback.projectId,
        this.feedback.rating,
        this.feedback.message,
      );
      this.api['snack'].showSuccess('Thank you for your feedback!');
      form.resetForm();
      this.feedback = { projectId: null, rating: 0, message: '' };
    } catch (err) {
      console.error('Feedback error:', err);
      this.api['snack'].showError('Failed to submit feedback.');
    }
  }

  selectMenu(menu: any, index: number) {
    // A dialog is not a route — we intentionally do NOT touch the URL here.
    // (Previously this called location.replaceState('/settings?tab=…'), which
    // faked a /settings URL that has no matching route; on close the router
    // reconciled to it, failed to match, and fell through to the homepage.)
    this.selectedMenu = menu;
    this.selectedMenuIndex = index;
  }

  // Handle image upload
  async onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    try {
      await this.api.uploadUserPhoto(file);
    } catch (err) {
      console.error('Upload failed:', err);
    }
  }

  // Optionally handle delete photo
  async onDeletePhoto() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '350px',
      data: {
        question: 'Are you sure you want to delete your profile photo?',
        confirmText: 'Delete',
        cancelText: 'Cancel',
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed) => {
      if (confirmed) {
        try {
          await this.api.deleteUserPhoto(); // 🔹 delete from storage + clear Firestore
        } catch (err) {
          console.error('Delete failed:', err);
        }
      }
    });
  }

  ticket = {
    subject: '',
    description: '',
    projectId: null,
  };

  async submitTicket(form: any) {
    if (this.ticket.subject && this.ticket.description) {
      try {
        await this.api.createTicket(
          this.ticket.subject,
          this.ticket.description,
          this.ticket.projectId,
        );

        this.api['snack'].showSuccess(
          'Your support ticket has been submitted.',
        );

        // ✅ Reset both form values and validation state
        form.resetForm();
      } catch (err: any) {
        console.error('Ticket error:', err);
        this.api['snack'].showError(
          err?.message || 'Failed to submit support ticket.',
        );
      }
    } else {
      this.api['snack'].showError(
        'Please fill in both subject and description.',
      );
    }
  }
  async toggleTicketStatus(ticket: any) {
    const newStatus = ticket.status === 'open' ? 'closed' : 'open';
    await this.api.updateTicketStatus(ticket.id, newStatus);
  }

  async deleteTicket(ticketId: string) {
    await this.api.softDeleteTicket(ticketId);
  }

  pay(amount: number) {
    const creditsToBuy = 100;

    this.paymentService.createOrder(amount, creditsToBuy).subscribe({
      next: async (orderResponse: any) => {
        console.log('✅ Order created for Angularize:', orderResponse);

        const { id, amount: rzpAmount, currency } = orderResponse.data;
        const uid = this.userId || 'unknown_user';

        const options: any = {
          key: 'rzp_live_SC80fSb6GXUXh6', // Your Public Test Key
          // key: 'rzp_test_RKxEV292tGkTPd', // Your Public Test Key
          amount: rzpAmount,
          currency,
          name: 'Angularize.dev', // Updated Brand Name
          description: `Add ${creditsToBuy} Credits to your account`,
          order_id: id,
          handler: (response: any) => {
            console.log('🎉 Razorpay Success:', response);

            this.paymentStatusMessage = 'Verifying your payment...';
            this.isProcessingCredits = true;

            // Call verifyPayment to validate the signature
            this.paymentService.verifyPayment(response).subscribe({
              next: (verification: any) => {
                console.log('✅ Signature Verified:', verification);

                this.paymentStatusMessage =
                  '🎉 Payment Verified! Your credits are being updated.';
                this.isProcessingCredits = false;

                setTimeout(() => (this.paymentStatusMessage = null), 5000);
              },
              error: (err) => {
                console.error('❌ Verification Failed:', err);
                this.paymentStatusMessage =
                  '⚠️ Verification failed. Checking server status...';
                this.isProcessingCredits = false;
              },
            });
          },
          modal: {
            ondismiss: () => {
              console.warn('⚠️ Checkout closed');
              this.paymentStatusMessage = 'Payment cancelled.';
              setTimeout(() => (this.paymentStatusMessage = null), 3000);
            },
          },
          prefill: {
            name: this.username || 'Angularize User',
            email: this.userEmail || '',
          },
          theme: {
            color: '#000000', // Sleek black theme for Angularize
          },
        };

        const rzp = new (window as any).Razorpay(options);

        rzp.on('payment.failed', (response: any) => {
          console.error('❌ Payment failed:', response.error);
          this.paymentStatusMessage = `❌ Payment Failed: ${response.error.description}`;
        });

        rzp.open();
      },
      error: (err) => {
        console.error('❌ Could not initiate order:', err);
        this.paymentStatusMessage =
          'Unable to start payment. Please try again.';
      },
    });
  }

  async restoreProject(project: any) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '350px',
      data: {
        question: `Are you sure you want to restore "${project.name}"?`,
        confirmText: 'Restore',
        cancelText: 'Cancel',
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed) => {
      if (confirmed) {
        try {
          await this.api.updateProject(project.project_id, {
            isDeleted: false,
          });
          this.api['snack'].showSuccess('Project restored successfully!');
        } catch (err: any) {
          console.error('Restore failed:', err);
          this.api['snack'].showError('Failed to restore project');
        }
      }
    });
  }
}